import React, { useState, useEffect } from 'react';

interface Question {
  id: number;
  text: string;
  options: string[];
  correctAnswer: number;
}

const QuizGame: React.FC = () => {
  // Kérdések
  const questions: Question[] = [
    {
      id: 1,
      text: 'Mit nevezünk tanyának?',
      options: ['A magányos mezőgazdasági telepeket', 'A társasházakat', 'Az istállót'],
      correctAnswer: 0
    },
    {
      id: 2,
      text: 'Tudta-e, hogy a szürkemarha kicsinyének születésekor nem szürke színe van, hanem „pirók”, és majd 4-5 hónaposan válik szürkévé? Ez azt jelenti, hogy a megszületett boci:',
      options: ['Zöld színű', 'Vörös színű', 'Lila színű'],
      correctAnswer: 1
    },
    {
      id: 3,
      text: 'Miért épült a tanyaépület a földterület legmagasabb pontjára?',
      options: ['Azért, hogy tanyán lakók jó messzire elláthassanak', 'Mert így nagyobb esőzéskor a víz átfolyt az udvaron és nem állt meg a ház tövében', 'Ha kinéztek az ablakon, rögtön ráláttak az állatokra'],
      correctAnswer: 1
    },
    {
        id: 4,
        text: 'Milyen nevei vannak a kecske kicsinyének?',
        options: ['Gödölye, gida', 'Kölyök, lurkó', 'Borjú, csikó'],
        correctAnswer: 0
      },
    {
        id: 5,
        text: 'Miben tárolták a nagy mennyiségű tengerit (kukoricát)?',
        options: ['Kútban', 'Disznóólban', 'Góréban'],
        correctAnswer: 2
    },
    {
        id: 6,
        text: 'Tudta-e, hogy a gulyások a szürkemarhákat: bajusz, búbos, csákó, fúró, gallyas, horgas, kontyos, kukó, lombár, pörge, saroglyás, sodró, táblás, tulipán, stb. jelzőkkel illetik. Ezek a szavak azt jelölik, hogy: ',
        options: ['Milyen alakú az egyes egyedek szarvállása', 'Milyen alakú a marha testfelépítése', 'Milyen alakú az egyed patája'],
        correctAnswer: 0
    },
    {
        id: 7,
        text: 'Mivel jelezte a gazdasszony a férjének, hogy kész az ebéd, amikor az messze kint a szántóföldeken dolgozott?',
        options: ['Felhívta telefonon', 'Füstjeleket küldött', 'Felengedte a gémeskút kútostorát, ami jó messzire ellátszott'],
        correctAnswer: 2
    },
    {
        id: 8,
        text: 'Fekete István regényének főhőse, Bogáncs milyen magyar kutyafajta volt?',
        options: ['Puli', 'Kuvasz', 'Pumi'],
        correctAnswer: 2
      },
    {
        id: 9,
        text: 'Melyik munkát végezte a tanyán a férfi?',
        options: ['Libaúsztatást', 'Lovak csutakolását', 'Szövést-fonást'],
        correctAnswer: 1
    },
    {
        id: 10,
        text: 'Tudta-e, hogy őseink eredetilege a „juh” szót csakis a rackára értették? Milyen eredetű szó ez?',
        options: ['Iráni', 'Görög', 'Finnugor'],
        correctAnswer: 2
    },
  ];

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [score, setScore] = useState(0);
  const [showFeedback, setShowFeedback] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);

  const currentQuestion = questions[currentQuestionIndex];

  const handleAnswerSelect = (optionIndex: number) => {
    if (showFeedback) return; // Ne engedjük a választ módosítani feedback közben
    
    setSelectedAnswer(optionIndex);
    setShowFeedback(true);

    if (optionIndex === currentQuestion.correctAnswer) {
      setScore(score + 1);
    }
  };

  const handleNextQuestion = () => {
    setSelectedAnswer(null);
    setShowFeedback(false);
    
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    } else {
      setQuizCompleted(true);
    }
  };

  const resetQuiz = () => {
    setCurrentQuestionIndex(0);
    setSelectedAnswer(null);
    setShowFeedback(false);
    setScore(0);
    setQuizCompleted(false);
  };

  const getButtonClass = (optionIndex: number) => {
    if (!showFeedback) return 'bg-gray-200 hover:bg-gray-300';
    
    if (optionIndex === currentQuestion.correctAnswer) {
      return 'bg-green-500 text-white';
    }
    
    if (optionIndex === selectedAnswer && optionIndex !== currentQuestion.correctAnswer) {
      return 'bg-red-500 text-white';
    }
    
    return 'bg-gray-200';
  };

  return (
    <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow-md">
      {quizCompleted ? (
        <div className="text-center">
          <h3 className="text-2xl font-bold mb-4">Gratulálunk!</h3>
          <h6 className="text-lg mb-6">A végső pontszámod: {score} / {questions.length}</h6>
          <button
            onClick={resetQuiz}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Újra játszom!
          </button>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-2">Kérdés {currentQuestionIndex + 1}/{questions.length}</h3>
            <p className="text-lg mb-4">{currentQuestion.text}</p>
            
            <div className="space-y-3">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswerSelect(index)}
                  disabled={showFeedback}
                  className={`w-full text-left px-4 py-2 rounded transition-colors ${getButtonClass(index)}`}
                >
                  {String.fromCharCode(65 + index)}:  {option}
                </button>
              ))}
            </div>
          </div>
          
          {showFeedback && (
            <div className="flex justify-between items-center">
              <p className="font-medium">
                {selectedAnswer === currentQuestion.correctAnswer ? (
                  <h4 className="text-green-600">Helyes válasz!</h4>
                ) : (
                  <h5 className="text-red-600">Sajnos rossz választ adtál! Helyes válasz: {currentQuestion.options[currentQuestion.correctAnswer]}</h5>
                )}
              </p>
              <button
                onClick={handleNextQuestion}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              >
                Következő kérdés
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default QuizGame;